import {
  Avatar,
  Box,
  Button,
  Flex,
  Input,
  PageGridContainer,
  SimpleGrid,
  Text,
  useAlgoliaSearch,
} from "@govlaunch/web";
import Link from "next/link";
import React, { useState } from "react";
import { useDebounce } from "react-use";
import NavbarLayout from "~/src/components/navbar/NavbarLayout";
import HomepageNavigation from "~/src/components/home/HomepageNavigation";
import { FormattedMessage, useIntl } from "react-intl";
import TranslationLinkButton from "~/src/components/translation/TranslationLinkButton";
import { useIntlLocale } from "~/lib/withIntl";
import VendorsPageSeo from "~/components/seo/VendorsPageSeo";

interface IVendorAlgoliaHit {
  objectID: string;
  _id: string;
  name: string;
  slug: string;
  logo: string;
  description: string;
  usersCanRequestContentTranslation: boolean | null | undefined;
}

interface IVendorsProps {
  // eslint-disable-next-line
  // @ts-ignore
  initialResults: algoliasearch.Response<IVendorAlgoliaHit>;
}

const VendorsPage = ({ initialResults }: IVendorsProps) => {
  const [vendors, setVendors] = useState<IVendorAlgoliaHit[]>(initialResults.hits);
  const { locale } = useIntlLocale();

  function concatMoreVendors(nextVendors: IVendorAlgoliaHit[]) {
    return setVendors((currentVendors) => {
      return currentVendors.concat(nextVendors);
    });
  }

  const { results, loading, setSearchState, searchState } = useAlgoliaSearch<IVendorAlgoliaHit>({
    initialResults,
    initialSearchState: {
      index: "companies",
      hitsPerPage: 15,
    },
    onResults: ({ results }) => {
      if (results.page > 0) {
        concatMoreVendors(results.hits as any);

        return;
      }

      setVendors(results.hits as any);
    },
  });

  const [inputValue, setInputValue] = useState("");

  useDebounce(
    () => {
      setSearchState((currentState) => ({
        ...currentState,
        page: 0,
        query: inputValue,
      }));
    },
    300,
    [inputValue],
  );

  const intl = useIntl();

  return (
    <NavbarLayout>
      <VendorsPageSeo />
      <HomepageNavigation />

      <PageGridContainer>
        <Box bg={["none", "none", "blue.100"]} px={[0, 0, 10]} py={3}>
          <Input
            aria-label="Search for vendors"
            placeholder={intl.formatMessage({
              defaultMessage: "Search for vendors...",
              id: "wc+cGC",
            })}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        </Box>

        <SimpleGrid columns={[1, 1, 2, 3]} spacing={6} mt={6}>
          {vendors.map((vendor) => (
            <Flex key={vendor.slug} borderRadius="md" border="1px" borderColor="gray.100" bg="white" direction="column">
              {locale !== "en-US" &&
                (typeof vendor.usersCanRequestContentTranslation === "undefined" ||
                  vendor.usersCanRequestContentTranslation === null ||
                  vendor.usersCanRequestContentTranslation) && (
                  <div
                    css={{
                      alignSelf: "flex-end",
                      marginTop: "3px",
                      marginRight: "3px",
                      marginBottom: "-10px",
                    }}
                  >
                    <Link
                      href={`/vendors/${vendor.slug}/${locale}`}
                      as={`/vendors/${vendor.slug}?lang=${locale}`}
                      passHref={true}
                      key={vendor._id}
                      scroll={false}
                    >
                      <TranslationLinkButton paddingY={1} />
                    </Link>
                  </div>
                )}
              <Link href={`/vendor?vendorSlug=${vendor.slug}`} as={`/vendors/${vendor.slug}`} passHref={true}>
                <Box as="a" display="flex" alignItems="center" p={4} overflow="hidden">
                  <Avatar src={vendor.logo} name={vendor.name} size="xl" mr={6} />
                  <Box>
                    <Text color="blue.600" fontSize="lg" fontWeight="semibold" mb={2} clamp={2}>
                      {vendor.name}
                    </Text>
                    <Text color="gray.900" fontSize="md" clamp={4}>
                      {vendor.description}
                    </Text>
                  </Box>
                </Box>
              </Link>
            </Flex>
          ))}
        </SimpleGrid>
        {(searchState.page || 0) + 1 < (results?.nbPages || 0) && (
          <Box display="flex" justifyContent="center" py={6}>
            <Button
              variant="secondary"
              loading={loading}
              size="sm"
              rightIcon={(props) => (
                <svg width="24" height="12" viewBox="0 0 24 12" fill="none" {...props}>
                  <path
                    d="M2 2.5L11.7436 9.55882L22 2.5"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              onClick={() => {
                setSearchState((currentState) => {
                  return {
                    ...currentState,
                    page: (currentState.page || 0) + 1,
                  };
                });
              }}
            >
              <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
            </Button>
          </Box>
        )}
      </PageGridContainer>
    </NavbarLayout>
  );
};

export default VendorsPage;
